/**
 * Stripe Billing Portal access.
 *
 * The backend's GET /webhooks/portal_session creates a pre-authenticated
 * Billing Portal session for the signed-in user, so they land directly in
 * the portal instead of on the static billing.stripe.com login page (which
 * makes them re-verify their email).
 *
 * The static STRIPE_CUSTOMER_PORTAL login link remains the fallback for
 * signed-out users, popup-blocked windows, and any backend failure, so the
 * billing portal is always reachable.
 */
import type { MouseEvent as ReactMouseEvent } from "react";
import { env, STRIPE_CUSTOMER_PORTAL } from "./GlobalStateProvider";
import { getApiUrl } from "../config";
import { getToken } from "./tokenManager";
import { isTrustedRedirectUrl, safeRedirect } from "./validateRedirectUrl";

/**
 * Fetches a pre-authenticated Billing Portal session URL for the current
 * user. Returns `null` when signed out or when the backend can't create a
 * session (no Stripe customer yet, network error, etc.).
 */
export async function fetchPortalSessionUrl(): Promise<string | null> {
  const token = getToken();
  if (!token) return null;

  // Bound the wait so a hung request falls back to the static link instead
  // of stranding the pre-opened tab on about:blank. AbortController +
  // setTimeout works across every browser in the production browserslist
  // (AbortSignal.timeout has gaps in older Safari/Firefox).
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 8000);

  try {
    const response = await fetch(`${getApiUrl(env)}/webhooks/portal_session`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
    if (!response.ok) return null;

    const data = await response.json();
    if (typeof data?.url === "string" && isTrustedRedirectUrl(data.url)) {
      return data.url;
    }
    return null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Click handler for `<Link href={STRIPE_CUSTOMER_PORTAL} target="_blank">`
 * elements. Upgrades the static-link click to a pre-authenticated portal
 * session while keeping the static link as href so middle-click, cmd-click,
 * and copy-link-address still work.
 *
 * Opens the tab synchronously (within the user gesture) to avoid popup
 * blockers, then steers it to the session URL once fetched.
 */
export function openPortalOnClick(e: ReactMouseEvent): void {
  // Let modified clicks and non-primary buttons fall through to the href.
  if (e.defaultPrevented) return;
  if (e.button !== 0) return;
  if (e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) return;

  if (!getToken()) return; // signed out: static login link is correct

  e.preventDefault();

  // Open now, while still inside the user gesture, so popup blockers allow it.
  const portalWindow = window.open("about:blank", "_blank");
  // Sever the reverse reference (defense-in-depth against tab-nabbing);
  // we keep steering the tab through our own portalWindow handle.
  if (portalWindow) portalWindow.opener = null;

  fetchPortalSessionUrl().then((url) => {
    const destination = url ?? STRIPE_CUSTOMER_PORTAL;
    if (portalWindow) {
      portalWindow.location.href = destination;
    } else {
      // Popup was blocked even within the gesture; navigate this tab instead.
      safeRedirect(destination);
    }
  });
}

/**
 * Same-tab variant for flows that redirect rather than open a new tab
 * (e.g. plan changes routed through the portal).
 */
export async function redirectToPortal(): Promise<void> {
  const url = await fetchPortalSessionUrl();
  safeRedirect(url ?? STRIPE_CUSTOMER_PORTAL);
}
